import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"

import { DEFAULT_TOAST_TIMEOUT } from "shared/constants/app-constants"
import { useCurrentOverlay } from "shared/context/overlay/OverlayIDContext"
import { raiseToast } from "shared/lib/toast/raiseToast"
import { useEvent } from "shared/lib/useEvent"
import { ToastType } from "shared/types/Toast"

import { useFeedback } from "features/feedback/model/useFeedback"

import {
  FeedbackOverlayFormData,
  FeedbackOverlayModel
} from "../FeedbackOverlay"
import { FeedbackOverlayConnectorProps } from "../FeedbackOverlayConnector"

export function useFeedbackOverlayModel({
  payload
}: FeedbackOverlayConnectorProps): FeedbackOverlayModel {
  const { closeOverlay } = useCurrentOverlay()
  const { feedbackType, message, attachments } = payload
  const { t } = useTranslation()
  const { sendFeedback, openFeedbackOverlay } = useFeedback()

  const handleSend = useEvent((formData: FeedbackOverlayFormData) => {
    if (feedbackType && formData.message) {
      const loadingToast = raiseToast({
        message: t("Feedback.Loading"),
        type: ToastType.LOADING,
        autoClose: false
      })

      const handleSuccess = () => {
        toast.update(loadingToast, {
          autoClose: DEFAULT_TOAST_TIMEOUT,
          data: {
            message: t("Feedback.Thanks"),
            type: ToastType.SUCCESS
          }
        })
      }

      const handleFailure = () => {
        toast.update(loadingToast, {
          data: {
            autoClose: DEFAULT_TOAST_TIMEOUT,
            message: t("Feedback.FeedbackError"),
            type: ToastType.ERROR
          }
        })
        openFeedbackOverlay(
          {
            feedbackType,
            message: formData.message,
            attachments: formData.attachments
          },
          { focusElementID: "feedback-button" }
        )
      }

      sendFeedback({
        feedbackType,
        message: formData.message,
        link: window.location.href,
        attachments: formData.attachments
      })
        .then(response => {
          if (response?.ok) {
            handleSuccess()
          } else {
            handleFailure()
          }
        })
        // TODO: This is a temporary fix for ISH. Retreive Feedback from Honeybadger if errors occur.
        .catch(() => handleSuccess())
      // .catch(handleFailure)
    }
    closeOverlay()
  })

  const defaultFormData = useMemo<FeedbackOverlayFormData>(
    () => ({
      message,
      attachments
    }),
    [message, attachments]
  )

  return {
    feedbackType,
    defaultFormData,
    sendFeedback: handleSend,
    onCancel: closeOverlay
  }
}
